// Selection normalization + aiCache key construction, shared byte-for-byte
// between the client (which computes the key for the reactive cache check) and
// the `convex/ai.ts` action (which recomputes it server-side). Ported from
// lekterion's `src/lib/model/{normalize,hash}.ts` + `convex/cacheKey.ts`, whose
// whole point is that equivalent selections collapse to one cache row.
//
// Pure module (no Convex/DOM imports): `crypto.subtle` is available in the
// browser, the Convex V8 runtime, and Bun alike, so the same hash is produced
// everywhere. Keep it free of `@/…` or `./_generated/…` imports so both sides
// can depend on it.

import { sha256Hex } from "@bindery/shared/crypto";
import { foldTypographicQuotes } from "./wordGloss";

/**
 * The slice of a `DocumentModel` selection normalization needs: the immutable
 * text and the word-like token overlay (phase 2 C3's tokenizer). Declared
 * structurally so the client `DocumentModel` and any server caller satisfy it
 * without a shared class.
 */
export interface SelectionModel {
  text: string;
  tokens: readonly { start: number; end: number }[];
}

/** A selection snapped to whole-token boundaries: the canonical quote + span. */
export interface NormalizedSpan {
  start: number;
  end: number;
  quote: string;
}

/**
 * Snap a raw `[selStart, selEnd)` selection (or a zero-width caret when they are
 * equal) outward to whole tokens, then return `text.slice(firstToken.start,
 * lastToken.end)`. Token boundaries already exclude surrounding
 * whitespace/punctuation, so this both trims and canonicalizes: a partial
 * in-word drag, the same word with a trailing comma, or a caret inside the word
 * all normalize to the same quote — which is what makes the cache key collapse.
 * Returns `null` when no token is touched (a gap/margin selection).
 */
export function normalizeSelection(
  model: SelectionModel,
  selStart: number,
  selEnd: number,
): NormalizedSpan | null {
  const lo = Math.min(selStart, selEnd);
  const hi = Math.max(selStart, selEnd);
  const caret = lo === hi;

  let firstStart = Number.POSITIVE_INFINITY;
  let lastEnd = -1;

  for (const t of model.tokens) {
    if (t.start >= hi && !caret) break; // tokens are sorted; nothing further intersects
    const intersects = t.start < hi && t.end > lo;
    const caretInside = caret && t.start <= lo && lo < t.end;
    if (intersects || caretInside) {
      if (t.start < firstStart) firstStart = t.start;
      if (t.end > lastEnd) lastEnd = t.end;
    }
    if (caret && t.start > lo) break;
  }

  if (lastEnd < 0) return null;
  const quote = model.text.slice(firstStart, lastEnd);
  if (quote.length === 0) return null;
  return { start: firstStart, end: lastEnd, quote };
}

/**
 * The slice of a `DocumentModel` the containing-context lookup needs: the text
 * and the block overlay. Structural, like `SelectionModel`.
 */
export interface BlockModel {
  text: string;
  blocks: readonly { start: number; end: number }[];
}

/**
 * Text of every block a normalized span touches, as one slice running from the
 * first touched block's start to the last one's end — the passage
 * `ai.glossSelection` glosses a quote in, cut by `passageAt` from the server's
 * copy and by the popover from its own for the up-front input check.
 *
 * Spanning to the LAST touched block is what keeps the containment guard
 * (`convex/model/glossInput.ts`) satisfiable. Originals are split one
 * block per verse line (`scripts/ingest/verseLines.ts`), and each line's block
 * ends BEFORE its newline, so a selection dragged across two lines normalizes
 * to a quote carrying that newline. Returning only the block under
 * `span.start` therefore produced a `paragraph` the quote was not a substring
 * of, and every cross-line gloss died on `quote_not_contained`.
 *
 * Assumes `blocks` is sorted by `start` (the ingest pipeline's order), as
 * `normalizeSelection` assumes of `tokens`. Returns `""` when the span touches
 * no block.
 */
export function containingBlocks(
  model: BlockModel,
  span: { start: number; end: number },
): string {
  let lo = Number.POSITIVE_INFINITY;
  let hi = -1;
  for (const block of model.blocks) {
    if (block.start >= span.end) break; // sorted; nothing further intersects
    if (block.end <= span.start) continue;
    if (block.start < lo) lo = block.start;
    if (block.end > hi) hi = block.end;
  }
  return hi < 0 ? "" : model.text.slice(lo, hi);
}

/**
 * The passage `ai.glossSelection` sends to Claude: `containingBlocks` of the
 * span in the server's own copy of the original, or `null` unless the span is
 * an in-range slice of that text spelling `quote` exactly.
 */
export function passageAt(
  original: BlockModel,
  span: { start: number; end: number },
  quote: string,
): string | null {
  const { start, end } = span;
  const inRange =
    Number.isInteger(start) &&
    Number.isInteger(end) &&
    start >= 0 &&
    start < end &&
    end <= original.text.length;
  if (!inRange || original.text.slice(start, end) !== quote) return null;
  const paragraph = containingBlocks(original, span);
  return paragraph === "" ? null : paragraph;
}

/** Count whole tokens fully inside `[start, end)` — 1 ⇒ a single-word span. */
export function spanTokenCount(
  model: SelectionModel,
  start: number,
  end: number,
): number {
  let n = 0;
  for (const t of model.tokens) {
    if (t.start >= end) break;
    if (t.start >= start && t.end <= end) n++;
  }
  return n;
}

/**
 * SHA-256 hex of the normalized quote — the `aiCache.selectionHash` column.
 * Hashing the quote alone (citation and targetLang are separate index columns,
 * `convex/schema.ts`) keeps equivalent selections on one row. Async because Web
 * Crypto is async; both the client key computation and the action await it.
 *
 * Quote marks are flattened to ASCII first, and ONLY those: case and every
 * other character are part of the key. The reader's text is educated
 * (`curlQuotes`) while the corpus is authored straight, and a passage does not
 * become a different passage — or a second paid Anthropic call — because its
 * apostrophes are typographic.
 */
export async function selectionHash(quote: string): Promise<string> {
  return sha256Hex(foldTypographicQuotes(quote));
}

/**
 * The `aiCache.citation` column — where a selection lives, e.g. `"odyssey/3"`.
 *
 * **PD-3 single-touch point.** Citation is chapter-granular per the accepted
 * default (schema comment): the same normalized selection at two spots in one
 * chapter shares a cache row. If PD-3 later refines to a finer locus, only this
 * helper and the `aiCache` schema comment change — every call site routes
 * through here so the key stays consistent across client and action.
 */
export function citationFor(bookSlug: string, chapterNumber: number): string {
  return `${bookSlug}/${String(chapterNumber)}`;
}
